/**
 * Catalog tab — product catalog management with role-aware affordances.
 *
 *   - Platform: list every product (filter by vendor); add a new product
 *               for any vendor; edit any item's basics.
 *   - Vendor:   list/edit only your own products; add new ones (auto-
 *               scoped to your company).
 *   - Customer: read-only list of products from vendors associated with
 *               your smartboxes. No add / no edit.
 *
 * The Items admin tab (critical flag + lead-time override) is a separate
 * concern — vendors who don't hold tags.manage still need this view.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import CreatePartForm from "../catalog/CreatePartForm";
import {
  type CatalogItem,
  type CatalogPage,
  type UpdateItemBody,
  type VendorOption,
  fetchCatalog,
  fetchCatalogVendors,
  setCatalogItemActive,
  updateCatalogItem,
} from "../../services/catalogApi";

const PAGE_SIZE = 100;

export const CatalogTab: React.FC = () => {
  const { token, role } = useAuth();
  const canCreate = role === "platform" || role === "vendor";
  const canEdit = role === "platform" || role === "vendor";
  // Only platform users can see deactivated parts (server ignores the flag for
  // everyone else), so only they get the control. See migration 120.
  const canSeeInactive = role === "platform";
  const [showInactive, setShowInactive] = useState(false);

  const [page, setPage] = useState<CatalogPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [vendorFilter, setVendorFilter] = useState<number | "">("");
  const [offset, setOffset] = useState(0);

  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const p = await fetchCatalog(token, {
        q: q || undefined,
        vendor: vendorFilter === "" ? undefined : vendorFilter,
        includeInactive: canSeeInactive && showInactive,
        limit: PAGE_SIZE,
        offset,
      });
      setPage(p);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [token, q, vendorFilter, offset, canSeeInactive, showInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!token) return;
    fetchCatalogVendors(token)
      .then(setVendors)
      .catch(() => setVendors([]));
  }, [token]);

  const applyEdit = (updated: CatalogItem): void => {
    if (!page) return;
    setPage({
      ...page,
      items: page.items.map((i) =>
        i.item_id === updated.item_id ? updated : i,
      ),
    });
  };

  const onCreated = (created: CatalogItem): void => {
    setShowCreate(false);
    // Insert at the top of the current page for quick visual confirmation.
    if (page) {
      setPage({ ...page, total: page.total + 1, items: [created, ...page.items] });
    } else {
      void load();
    }
  };

  const onSearchSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setOffset(0);
    setQ(qInput.trim());
  };

  const showVendorPicker = vendors.length > 1; // hide for a vendor-of-one

  return (
    <div className="space-y-3 max-w-6xl">
      {/* Filter bar */}
      <form
        onSubmit={onSearchSubmit}
        className="flex flex-wrap items-end gap-2 bg-secondary-50 border border-secondary-200 rounded-md p-3"
      >
        <label className="text-xs text-secondary-600 flex-1 min-w-[200px]">
          <div>Search SKU / name / OEM #</div>
          <input
            type="text"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="e.g. valve, plunger, 0740"
            className="w-full border border-secondary-300 rounded px-2 py-1 text-sm"
          />
        </label>
        <Button type="submit" size="sm">
          Search
        </Button>
        {showVendorPicker && (
          <label className="text-xs text-secondary-600">
            <div>Vendor</div>
            <select
              value={vendorFilter === "" ? "" : String(vendorFilter)}
              onChange={(e) => {
                setOffset(0);
                setVendorFilter(e.target.value === "" ? "" : Number(e.target.value));
              }}
              className="border border-secondary-300 rounded px-2 py-1 text-sm bg-white min-w-[200px]"
            >
              <option value="">— All vendors —</option>
              {vendors.map((v) => (
                <option key={v.company_id} value={v.company_id}>
                  {v.company_name}
                </option>
              ))}
            </select>
          </label>
        )}
        {canCreate && (
          <Button
            type="button"
            size="sm"
            onClick={() => setShowCreate((v) => !v)}
            variant={showCreate ? "outline" : "default"}
          >
            {showCreate ? "Cancel" : "Add product"}
          </Button>
        )}
      </form>

      {showCreate && canCreate && (
        <CreatePartForm
          vendors={vendors}
          onCreated={onCreated}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {error && (
        <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">
          {error}
        </div>
      )}

      {/* Header / pagination */}
      {page && (
        <div className="flex items-center justify-between text-xs text-secondary-600">
          <span>
            <strong>{page.total.toLocaleString()}</strong> product
            {page.total === 1 ? "" : "s"}
            {q && <> matching "{q}"</>}
            {page.total > 0 && (
              <>
                {" "}
                — showing {offset + 1}–
                {Math.min(offset + page.items.length, page.total)}
              </>
            )}
          </span>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0 || loading}
            >
              ← Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= page.total || loading}
            >
              Next →
            </Button>
          </div>
        </div>
      )}

      {canSeeInactive && (
        <label className="flex items-center gap-2 text-xs text-secondary-600 mb-2 select-none">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => {
              setOffset(0);
              setShowInactive(e.target.checked);
            }}
          />
          Show deactivated products
          <span className="text-secondary-400">
            (hidden from Match Tags and from customer/vendor lists)
          </span>
        </label>
      )}

      {/* Table */}
      <div className="bg-white border border-secondary-200 rounded-md overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-secondary-50 text-secondary-700 uppercase">
            <tr>
              <th className="text-left px-3 py-2">SKU / Name</th>
              <th className="text-left px-3 py-2">Vendor</th>
              <th className="text-left px-3 py-2">Category</th>
              <th className="text-right px-3 py-2">Unit price</th>
              <th className="text-right px-3 py-2">Lead time</th>
              {canEdit && <th className="text-right px-3 py-2">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-secondary-100">
            {loading && !page ? (
              <tr>
                <td colSpan={canEdit ? 6 : 5} className="px-3 py-6 text-center text-secondary-500">
                  loading…
                </td>
              </tr>
            ) : page && page.items.length === 0 ? (
              <tr>
                <td colSpan={canEdit ? 6 : 5} className="px-3 py-6 text-center text-secondary-500">
                  No products match those filters.
                </td>
              </tr>
            ) : (
              page?.items.map((item) => (
                <CatalogRow
                  key={item.item_id}
                  item={item}
                  canEdit={canEdit}
                  showVendorOnEdit={role === "platform"}
                  vendors={vendors}
                  onChange={applyEdit}
                  inactiveVisible={canSeeInactive && showInactive}
                  onReload={() => void load()}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CatalogTab;

// ---------------------------------------------------------------------------
// Row + inline edit
// ---------------------------------------------------------------------------

const fmtMoney = (n: number | null): string =>
  n == null ? "—" : `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const CatalogRow: React.FC<{
  item: CatalogItem;
  canEdit: boolean;
  showVendorOnEdit: boolean;
  vendors: VendorOption[];
  onChange: (next: CatalogItem) => void;
  /** True when deactivated rows are being shown, so a row that has just been
   *  deactivated can stay put instead of forcing a refetch. */
  inactiveVisible: boolean;
  onReload: () => void;
}> = ({
  item, canEdit, showVendorOnEdit, vendors, onChange, inactiveVisible, onReload,
}) => {
  const { token } = useAuth();
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sku, setSku] = useState(item.sku ?? "");
  const [name, setName] = useState(item.name ?? "");
  const [oem, setOem] = useState(item.oem_no ?? "");
  const [category, setCategory] = useState(item.category_name ?? "");
  const [price, setPrice] = useState(item.unit_price != null ? String(item.unit_price) : "");
  const [leadTime, setLeadTime] = useState(String(item.lead_time_days));
  const [vendorId, setVendorId] = useState<number | "">(item.company_id ?? "");

  const cancel = (): void => {
    setEditing(false);
    setErr(null);
    setSku(item.sku ?? "");
    setName(item.name ?? "");
    setOem(item.oem_no ?? "");
    setCategory(item.category_name ?? "");
    setPrice(item.unit_price != null ? String(item.unit_price) : "");
    setLeadTime(String(item.lead_time_days));
    setVendorId(item.company_id ?? "");
  };

  // Activate/deactivate. When a row is deactivated while deactivated rows are
  // hidden it must leave the page, so reload rather than patching in place —
  // otherwise the row lingers and the pagination total goes stale.
  const toggleActive = async (): Promise<void> => {
    if (!token) return;
    setBusy(true);
    setErr(null);
    try {
      const next = !item.is_active;
      const updated = await setCatalogItemActive(token, item.item_id, next);
      if (!next && !inactiveVisible) {
        onReload();
        return;
      }
      onChange(updated);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const save = async (): Promise<void> => {
    if (!token) return;
    setBusy(true);
    setErr(null);
    try {
      const body: UpdateItemBody = {};
      if (sku.trim() !== (item.sku ?? "")) body.sku = sku.trim();
      if (name.trim() !== (item.name ?? "")) body.name = name.trim();
      if (oem.trim() !== (item.oem_no ?? "")) body.oem_no = oem.trim() || null;
      if (category.trim() !== (item.category_name ?? "")) body.category_name = category.trim() || null;
      const newPrice = price === "" ? null : Number(price);
      if (newPrice !== item.unit_price) body.unit_price = newPrice;
      const newLead = Number(leadTime);
      if (Number.isFinite(newLead) && newLead !== item.lead_time_days) body.lead_time_days = newLead;
      if (showVendorOnEdit && vendorId !== "" && Number(vendorId) !== item.company_id) {
        body.vendor_company_id = Number(vendorId);
      }
      if (Object.keys(body).length === 0) {
        setEditing(false);
        setBusy(false);
        return;
      }
      const updated = await updateCatalogItem(token, item.item_id, body);
      onChange(updated);
      setEditing(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <tr
        className={`hover:bg-secondary-50 align-top ${
          item.is_active ? "" : "bg-secondary-50/60 text-secondary-400"
        }`}
      >
        <td className="px-3 py-2">
          <div className="font-mono font-semibold">
            {item.sku ?? "—"}
            {!item.is_active && (
              <span className="ml-2 font-sans font-normal text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-secondary-200 text-secondary-700">
                inactive
              </span>
            )}
          </div>
          {item.name && (
            <div className="text-secondary-500 text-[11px]">{item.name}</div>
          )}
          {item.oem_no && (
            <div className="text-secondary-400 text-[10px]">OEM: {item.oem_no}</div>
          )}
        </td>
        <td className="px-3 py-2 text-secondary-600">{item.company_name ?? "—"}</td>
        <td className="px-3 py-2 text-secondary-600">{item.category_name ?? "—"}</td>
        <td className="px-3 py-2 text-right font-mono">{fmtMoney(item.unit_price)}</td>
        <td className="px-3 py-2 text-right font-mono">{item.lead_time_days}d</td>
        {canEdit && (
          <td className="px-3 py-2 text-right whitespace-nowrap">
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              Edit
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="ml-1"
              disabled={busy}
              onClick={() => void toggleActive()}
              title={
                item.is_active
                  ? "Hide from Match Tags and from customer/vendor parts lists"
                  : "Make this product selectable again"
              }
            >
              {item.is_active ? "Deactivate" : "Reactivate"}
            </Button>
          </td>
        )}
      </tr>
    );
  }

  return (
    <tr className="bg-primary-50 align-top">
      <td className="px-3 py-2">
        <input
          value={sku}
          onChange={(e) => setSku(e.target.value)}
          className="w-full border border-secondary-300 rounded px-1.5 py-0.5 text-xs font-mono mb-1"
        />
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="name"
          className="w-full border border-secondary-300 rounded px-1.5 py-0.5 text-[11px]"
        />
        <input
          value={oem}
          onChange={(e) => setOem(e.target.value)}
          placeholder="OEM #"
          className="w-full border border-secondary-300 rounded px-1.5 py-0.5 text-[11px] mt-1"
        />
        {err && (
          <div className="mt-1 text-[11px] text-error-700">{err}</div>
        )}
      </td>
      <td className="px-3 py-2 text-secondary-600">
        {showVendorOnEdit ? (
          <select
            value={vendorId === "" ? "" : String(vendorId)}
            onChange={(e) => setVendorId(e.target.value === "" ? "" : Number(e.target.value))}
            className="w-full border border-secondary-300 rounded px-1.5 py-0.5 text-xs bg-white"
          >
            <option value="">— Pick a vendor —</option>
            {vendors.map((v) => (
              <option key={v.company_id} value={v.company_id}>
                {v.company_name}
              </option>
            ))}
          </select>
        ) : (
          <span title="Vendors cannot reassign their own items">
            {item.company_name ?? "—"}
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        <input
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-full border border-secondary-300 rounded px-1.5 py-0.5 text-xs"
        />
      </td>
      <td className="px-3 py-2 text-right">
        <input
          type="number"
          step="0.01"
          min={0}
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          className="w-20 border border-secondary-300 rounded px-1.5 py-0.5 text-xs font-mono text-right"
        />
      </td>
      <td className="px-3 py-2 text-right">
        <input
          type="number"
          step="1"
          min={0}
          max={3650}
          value={leadTime}
          onChange={(e) => setLeadTime(e.target.value)}
          className="w-16 border border-secondary-300 rounded px-1.5 py-0.5 text-xs font-mono text-right"
        />
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="outline" onClick={cancel} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => void save()} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </div>
      </td>
    </tr>
  );
};
